// Utility functions for handling HTML content

/**
 * Extracts plain text from HTML content, removing all tags and cleaning up
 * @param {string} html - HTML content
 * @param {number} maxLength - Maximum length of returned text (default: 150)
 * @returns {string} Clean plain text
 */
export const extractTextFromHtml = (html, maxLength = 150) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove script and style elements
  const scripts = tempDiv.querySelectorAll('script, style');
  scripts.forEach(element => element.remove());

  // Get text content
  let text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up whitespace
  text = text.replace(/\s+/g, ' ').trim();

  // Truncate if needed
  if (text.length > maxLength) {
    text = text.substring(0, maxLength) + '...';
  }

  return text;
};

/**
 * Strips long data URLs and replaces them with descriptive text
 * @param {string} html - HTML content
 * @returns {string} HTML with data URLs replaced
 */
export const stripLongDataUrls = (html) => {
  if (!html || typeof html !== 'string') {
    return html;
  }

  // Remove data URL images (since thumbnails already show the image)
  return html.replace(
    /<img[^>]+src="data:image\/[^"]{100,}"[^>]*>/gi,
    ''
  );
};

/**
 * Gets a clean preview of HTML content for display in lists
 * @param {string} html - HTML content
 * @param {number} maxLength - Maximum length (default: 100)
 * @returns {string} Clean preview text
 */
export const getContentPreview = (html, maxLength = 100) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // First strip audio markup [audio:clipName]
  let cleanHtml = html.replace(/\[audio:[^\]]+\]/g, '');
  
  // Then strip long data URLs
  cleanHtml = stripLongDataUrls(cleanHtml);
  
  // Then extract text
  return extractTextFromHtml(cleanHtml, maxLength);
};

/**
 * Checks if content contains embedded images
 * @param {string} html - HTML content
 * @returns {boolean} True if contains images
 */
export const hasEmbeddedImages = (html) => {
  if (!html || typeof html !== 'string') {
    return false;
  }

  return /<img[^>]+src="data:image/i.test(html);
};

/**
 * Gets content stats for display
 * @param {string} html - HTML content
 * @returns {object} Stats object with word count, image count, etc.
 */
export const getContentStats = (html) => {
  if (!html || typeof html !== 'string') {
    return { wordCount: 0, imageCount: 0, videoCount: 0, hasEmbeddedContent: false };
  }

  const text = extractTextFromHtml(html);
  const wordCount = text ? text.split(/\s+/).filter(word => word.length > 0).length : 0;
  
  const imageMatches = html.match(/<img[^>]*>/gi) || [];
  const imageCount = imageMatches.length;
  
  // Count videos (iframes)
  const videoMatches = html.match(/<iframe[^>]*>/gi) || [];
  const videoCount = videoMatches.length;
  
  const hasEmbeddedContent = hasEmbeddedImages(html) || imageCount > 0 || videoCount > 0;

  return {
    wordCount,
    imageCount,
    videoCount,
    hasEmbeddedContent
  };
};

/**
 * Extract the first image URL from HTML content
 * @param {string} html - HTML content
 * @returns {string|null} - First image URL or null if none found
 */
export const getFirstImageUrl = (html) => {
  if (!html || typeof html !== 'string') {
    return null;
  }
  
  // Match first img tag
  const imgMatch = html.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/i);
  if (imgMatch) {
    return imgMatch[1];
  }
  
  // Match first YouTube video thumbnail
  const youtubeMatch = html.match(/youtube\.com\/embed\/([^"'?\s]+)/i);
  if (youtubeMatch) {
    return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
  }
  
  // Match first Vimeo video thumbnail
  const vimeoMatch = html.match(/player\.vimeo\.com\/video\/(\d+)/i);
  if (vimeoMatch) {
    return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;
  }
  
  return null;
};